import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Sparkles, Plane, Car, PawPrint } from 'lucide-react';
import ItineraryMap, { buildMapLocations } from './ItineraryMap';
import { singaporeCoordinates, singaporeCenter } from './coordinates/singapore';

interface Activity {
  icon: React.ReactNode;
  title: string;
  description: string;
  time?: string;
  image?: string;
  location?: string;
  muted?: boolean;
}

interface Day {
  date: string;
  dayNumber: number;
  title: string;
  subtitle: string;
  theme: 'singapore-layover';
  activities: Activity[];
  highlight?: boolean;
}

const singaporeItinerary: Day[] = [
  {
    date: 'Wednesday, May 6',
    dayNumber: 1,
    title: 'Singapore Layover — Night Safari',
    subtitle:
      'Early arrival into Changi → Mandai for the Night Safari → straight back to Changi for the First lounge before the onward to Melbourne',
    theme: 'singapore-layover',
    highlight: true,
    activities: [
      {
        icon: <Plane size={24} />,
        title: 'Land Changi Airport — early',
        description:
          'Touchdown ahead of schedule. Australian passports are visa-free into Singapore, so you can leave the airport. Bags are already checked through to Melbourne — hand-luggage only on the city dash, just the kit-bag with running gear and a clean shirt. Auto-gates clear quickly; head straight out to the Grab pick-up at T1/T2/T3 (whichever terminal you arrive at).',
        location: 'Singapore Changi Airport',
        time: 'Afternoon',
        image: '/images/singapore/changi-airport.jpg',
        muted: true,
      },
      {
        icon: <Car size={24} />,
        title: 'Grab → Night Safari (Mandai)',
        description:
          'Grab/taxi straight from Changi up to 80 Mandai Lake Road. ~35 min via PIE/SLE depending on traffic, around S$30–40. Faster and far simpler than MRT (which would need 3 changes plus a bus). Tell the driver Mandai Wildlife Reserve / Night Safari entrance — the drop-off is right at the gates.',
        location: 'Singapore Changi Airport',
        time: '5:15 PM',
        muted: true,
      },
      {
        icon: <PawPrint size={24} />,
        title: 'Night Safari — Mandai',
        description:
          "World's first nocturnal wildlife park, set in 35 hectares of secondary rainforest. Arrive at 6:00 PM — gates open at 6:15 PM, tram safari starts running 7:15 PM. Plan the visit around the 40-minute tram ride (it threads through habitats with leopards, tapirs, hyenas, Asian elephants), plus one of the four walking trails — Fishing Cat Trail is the quickest and shows off civets, pangolins, and small carnivores. Pre-book tram + admission online to skip the ticket queue. Skip dinner here — you've got the First lounge waiting.",
        location: 'Night Safari Singapore',
        time: '6:00 PM',
        image: '/images/singapore/singapore-skyline.jpg',
      },
      {
        icon: <Car size={24} />,
        title: 'Grab → Changi',
        description:
          'Straight back to Changi from Mandai — Grab from the Night Safari pick-up, ~35 min via SLE/PIE at that hour, around S$30–35. Tap-in at the lounge by 9:00 PM with plenty of buffer for the 11:30 PM departure.',
        location: 'Night Safari Singapore',
        time: '8:00 PM',
        muted: true,
      },
      {
        icon: <Sparkles size={24} />,
        title: 'First lounge — dinner + decompress',
        description:
          'First lounge access at Changi handles dinner for both of you (this is why the city plan skips a restaurant). Coeliac order at the buffet — flag GF with the chef and stick to grilled proteins, salad, rice, fruit. Plenty of time before boarding — Matt can use the showers if wanted, but no urgency. Tea, not coffee — sleep matters on the onward leg.',
        location: 'Changi Airport',
        time: '9:00 PM',
        muted: true,
      },
      {
        icon: <Plane size={24} />,
        title: 'Onward — Singapore → Melbourne',
        description:
          'Boarding ~10:50 PM for the ~7 h 45 min hop to MEL. Coeliac (GFML) meal pre-ordered — reconfirm at the gate. Eat the airline meal lightly; Melbourne breakfast tomorrow morning.',
        location: 'Singapore Changi Airport',
        time: '11:30 PM',
        image: '/images/singapore/singapore-skyline.jpg',
        muted: true,
      },
    ],
  },
];

const SingaporeTab: React.FC = () => {
  return (
    <>
      <motion.section
        className="hero singapore-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
      >
        <div className="hero-background">
          <img src="/images/singapore/singapore-skyline.jpg" alt="Singapore Marina Bay" />
          <div className="hero-overlay singapore-overlay" />
        </div>
        <div className="hero-content">
          <motion.div
            className="hero-label"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <Calendar size={16} />
            <span>May 6, 2026</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            Singapore Layover
          </motion.h1>
          <motion.div
            className="hero-details"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <div className="hero-detail">
              <MapPin size={20} />
              <span>Changi → Night Safari → Changi</span>
            </div>
            <div className="hero-detail">
              <PawPrint size={20} />
              <span>Night Safari + First Lounge Dinner</span>
            </div>
          </motion.div>
        </div>
        <motion.div
          className="scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
        >
          <div className="scroll-line" />
        </motion.div>
      </motion.section>

      <section className="intro-section singapore-intro">
        <div className="intro-content">
          <h2>Singapore Layover</h2>
          <p>
            Early arrival into Changi means we trade the city dinner for a Mandai detour. Bags are checked through to
            Melbourne, so it&apos;s hand-luggage only. Grab straight from Changi up to the Night Safari for a 6:00 PM
            start — tram safari plus one walking trail covers the headlines in two hours.
          </p>
          <p>
            Out by 8:00 PM, Grab straight back to Changi (no Marina Bay detour). The First lounge handles dinner before
            the 11:30 PM onward to Melbourne — plenty of time to decompress.
          </p>
        </div>
      </section>

      <section className="timeline-section">
        <h2 className="section-title singapore-section-title">Layover Plan</h2>

        <div className="timeline">
          {singaporeItinerary.map((day, dayIndex) => (
            <div
              key={dayIndex}
              className={`day-card ${day.theme} ${day.highlight ? 'highlight singapore-highlight' : ''}`}
            >
              <div className="day-header">
                <div className="day-number">Day {day.dayNumber}</div>
                <div className="day-info">
                  <div className="day-date">{day.date}</div>
                  <h3 className="day-title">{day.title}</h3>
                  <p className="day-subtitle">{day.subtitle}</p>
                </div>
              </div>

              <div className="activities">
                {day.activities.map((activity, activityIndex) => (
                  <div
                    key={activityIndex}
                    className={`activity ${activity.muted ? 'muted' : ''} ${!activity.image ? 'no-image' : ''}`}
                  >
                    {activity.image && (
                      <div className="activity-image">
                        <img src={activity.image} alt={activity.title} />
                      </div>
                    )}
                    <div className="activity-content">
                      <div className="activity-header">
                        <div className="activity-icon singapore-icon">{activity.icon}</div>
                        <div className="activity-header-text">
                          <h4 className="activity-title">{activity.title}</h4>
                          {activity.time && <div className="activity-time singapore-time">{activity.time}</div>}
                        </div>
                      </div>
                      {activity.location && (
                        <div className="activity-location">
                          <MapPin size={12} />
                          <span>{activity.location}</span>
                        </div>
                      )}
                      <p className="activity-description">{activity.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <ItineraryMap
        locations={buildMapLocations(singaporeItinerary, singaporeCoordinates)}
        center={singaporeCenter}
        zoom={11}
        themeColor="#d97757"
        totalDays={1}
      />

      <section className="intro-section singapore-intro">
        <div className="intro-content">
          <h2>Layover Checklist</h2>
          <p>
            <strong>Night Safari tickets</strong> — Pre-book admission + tram online for a 6:00 PM entry. Tram safari is
            the headline; walking trails optional after.
          </p>
          <p>
            <strong>Grab app</strong> — Ride to Mandai and back to Changi runs on Grab. Have the app installed and a
            card attached before landing.
          </p>
          <p>
            <strong>Coeliac (GFML) meal — SIN→MEL</strong> — Pre-order with the airline. Reconfirm at the gate.
          </p>
          <p>
            <strong>Bags routed to MEL</strong> — Confirmed at outbound check-in. City dash is hand-luggage only.
          </p>
          <p>
            <strong>First lounge access</strong> — Confirmed on the SIN→MEL ticket. Dinner for both back at Changi.
          </p>
          <p>
            <strong>Payment</strong> — Tap-to-pay contactless card works for Grab, Night Safari, and any drinks. No need
            to buy an EZ-Link.
          </p>
        </div>
      </section>

      <motion.footer
        className="footer singapore-footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="footer-content">
          <Sparkles size={32} />
          <h3>Onward to Melbourne</h3>
          <p>May 6, 2026</p>
        </div>
      </motion.footer>
    </>
  );
};

export default SingaporeTab;
